import { supabase } from "../supabase";
import type { NotificationRow } from "./notify";
import type { TimelineItem } from "./timeline";

/** 一度に読む上限。これより古いものは辿らない(遡って読む機能は持たない) */
const INBOX_LIMIT = 200;

const COLUMNS = "id, kind, actor_id, media_id, body, created_at, read_at, event_id";

/** 自分宛の通知。新しい順 */
export async function fetchNotifications(): Promise<NotificationRow[]> {
  const { data, error } = await supabase
    .from("notifications")
    .select(COLUMNS)
    .order("created_at", { ascending: false })
    .limit(INBOX_LIMIT);
  if (error) throw error;
  const rows: NotificationRow[] = data;
  return rows;
}

/**
 * 未読だけ。バッジのために定期的に引くので、一覧とは分けて軽くしておく。
 * 普段は0件〜数件しか返らない。
 */
export async function fetchUnreadNotifications(): Promise<NotificationRow[]> {
  const { data, error } = await supabase
    .from("notifications")
    .select(COLUMNS)
    .is("read_at", null)
    .order("created_at", { ascending: false })
    .limit(INBOX_LIMIT);
  if (error) throw error;
  const rows: NotificationRow[] = data;
  return rows;
}

/** 指定した行だけ既読にする。押した通知の分だけ */
export async function markRead(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .in("id", ids)
    .is("read_at", null);
  if (error) throw error;
}

/** 未読を全部既読にする */
export async function markAllRead(): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .is("read_at", null);
  if (error) throw error;
}

/**
 * 通知の行に添えるサムネイルのキー。先頭の1枚だけ出すので、行ごとの代表を引く。
 * 消えたものは行ごと出さない(削除通知にはサムネイルを付けない)ため、欠けは正常。
 */
export async function fetchThumbnailKeys(
  mediaIds: string[],
): Promise<{ id: string; thumbnail_key: string }[]> {
  if (mediaIds.length === 0) return [];
  const { data, error } = await supabase
    .from("media")
    .select("id, thumbnail_key")
    .in("id", mediaIds);
  if (error) throw error;
  const rows: { id: string; thumbnail_key: string }[] = data;
  return rows;
}

/**
 * 通知から辿るためのメディア。
 *
 * **まとめた通知はまとめた分だけ返す。** 「99件追加しました」を押して1件しか
 * 見られないのでは、まとめた意味が無い。
 *
 * 並びはタイムラインと同じ(撮影日時の新しい順)にして、送り方を揃える。
 * 削除済みも引ける(ゴミ箱に入ったものを確かめられるように)。
 */
export async function fetchMediaByIds(mediaIds: string[]): Promise<TimelineItem[]> {
  if (mediaIds.length === 0) return [];
  const { data, error } = await supabase
    .from("media")
    .select("id, type, thumbnail_key, storage_key, duration_seconds, taken_at, uploader_id")
    .in("id", mediaIds)
    .order("taken_at", { ascending: false })
    .order("id", { ascending: false });
  if (error) throw error;
  const items: TimelineItem[] = data;
  return items;
}
